import { LmmTijdstip, LmmTijdstipLabel } from '../types/LmmVoorraad';

/** Returns a new Date set to the given hour (minutes/seconds/ms zeroed) on the same day as `from`. */
const atHour = (from: Date, hour: number): Date => {
  const date = new Date(from);
  date.setHours(hour, 0, 0, 0);
  return date;
};

/** Returns the next future occurrence (today or tomorrow) of the given hour, relative to `now`. */
const nextOccurrenceOfHour = (now: Date, hour: number): Date => {
  const candidate = atHour(now, hour);
  if (candidate.getTime() > now.getTime()) return candidate;
  const tomorrow = new Date(candidate);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
};

/**
 * Computes the 3 tijdstippen shown in the LMM table:
 * - before 08:00: Nu, 08:00 (today), 17:00 (today)
 * - from 08:00 onward: Nu, 17:00, 08:00 (next occurrence)
 *
 * This keeps the daytime/evening view in the expected business order (17:00 first, then next
 * morning 08:00), while still only looking forward in time.
 */
export const getLmmTijdstippen = (now: Date = new Date()): LmmTijdstip[] => {
  const nu: LmmTijdstip = { label: 'Nu', dateTime: now };
  const next08 = nextOccurrenceOfHour(now, 8);
  const next17 = nextOccurrenceOfHour(now, 17);
  const before08 = now.getHours() < 8;
  if (before08) {
    return [nu, { label: '08:00', dateTime: next08 }, { label: '17:00', dateTime: next17 }];
  }

  return [nu, { label: '17:00', dateTime: next17 }, { label: '08:00', dateTime: next08 }];
};

const isSameCalendarDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const getLmmTijdstip = (tijdstip: LmmTijdstip): string => {
  if (tijdstip.label.toUpperCase() === 'NU') return 'Nu';
  return tijdstip.label;
};
export const getLmmTijdstipDatum = (tijdstip: LmmTijdstip, now: Date = new Date()): string => {
  const dateSuffix = `(${tijdstip.dateTime.getDate()}-${tijdstip.dateTime.getMonth() + 1})`;
  return dateSuffix.includes('NaN') || isSameCalendarDay(tijdstip.dateTime, now) ? '' : dateSuffix;
};

/**
 * Formats the title for a tijdstip: 'Nu' as-is, otherwise 'HH:MM', with a compact '(D-M)' date
 * suffix appended when the tijdstip falls on a different calendar day than `now`.
 */
export const getLmmTijdstipTitel = (tijdstip: LmmTijdstip, now: Date = new Date()): string => {
  const timeLabel = getLmmTijdstip(tijdstip);
  if (isSameCalendarDay(tijdstip.dateTime, now)) return timeLabel;
  const dateSuffix = getLmmTijdstipDatum(tijdstip, now);
  /** Tijdelijke bug fix tot peildatumtijd weer wordt toegevoegd. */
  return `${timeLabel}${dateSuffix.includes('NaN') ? '' : ` ${dateSuffix}`}`;
};

export const getLmmTooltipTijdstipTitel = (tijdstip: LmmTijdstip, now: Date = new Date()): string => {
  if (tijdstip.label === '08:00' && !isSameCalendarDay(tijdstip.dateTime, now)) {
    return `Opstart ${tijdstip.dateTime.getDate()}-${tijdstip.dateTime.getMonth() + 1}`;
  }
  return getLmmTijdstipTitel(tijdstip, now);
};

export const isLmmTijdstipNu = (label: LmmTijdstipLabel): boolean => label === 'Nu';
